// Every user action becomes one undo step and one pending entry: an atomic group of record-level
// ops, each based on the record as this tab saw it (ADR 0011). Undo and Redo send the inverse ops
// through the same conditional path, so they can't overwrite someone else's newer change: if the
// record moved on, the server refuses the entry and it waits for review like any other conflict.

import { signal } from "@preact/signals";
import { recordHash } from "../format/ids.ts";
import { type Op, recordKey } from "../format/ops.ts";
import type { AnyRecord, Records, RecordType } from "../format/types.ts";
import { addPending, type PendingEntry } from "./idb.ts";
import { notify, pending, show, tabId } from "./state.ts";
import { kick } from "./sync.ts";

/** One record changing: created (no before), edited, or deleted (no after). */
export interface Change {
  type: RecordType;
  before?: AnyRecord;
  after?: AnyRecord;
}

interface Action {
  label: string;
  changes: Change[];
}

const LIMIT = 100;
export const undoStack = signal<Action[]>([]);
export const redoStack = signal<Action[]>([]);

/** Helpers to describe changes without repeating the type. */
export const put = <K extends RecordType>(type: K, after: Records[K], before?: Records[K]): Change => ({ type, after, before });
export const del = <K extends RecordType>(type: K, before: Records[K]): Change => ({ type, before });

export function entryId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(12));
  return `e-${Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")}`;
}

/** Ops for a set of changes. Each op's base is the record as this tab last saw it (`before`). */
export function toOps(changes: Change[]): Op[] {
  return changes.map((c) => {
    const rec = c.after ?? c.before;
    if (!rec) throw new Error("a change needs a record");
    const base = recordHash(c.type, c.before);
    return c.after ? { op: "put", type: c.type, base, record: c.after } : { op: "delete", type: c.type, base, key: recordKey(c.type, rec) };
  });
}

/** Queue ops as one entry: shown at once, sent only once IndexedDB holds it. */
export async function queue(label: string, ops: Op[], before: (AnyRecord | null)[], generation = show.value.generation): Promise<void> {
  if (!ops.length) return;
  const entry: PendingEntry = { id: entryId(), tab: tabId, label, ops, before, at: Date.now(), generation };
  pending.value = [...pending.value, entry];
  try {
    entry.seq = await addPending(entry);
  } catch (e) {
    notify(`This device couldn't store the edit (${(e as Error).message}). It will still be sent, but keep this tab open until it shows Saved.`, "warn");
    entry.seq = -Date.now();
  }
  pending.value = [...pending.value];
  kick();
}

/** Make an edit: one undo step. */
export function commit(label: string, changes: Change[]): void {
  if (show.value.lock) {
    notify(`${show.value.lock} This edit wasn't made.`, "error");
    return;
  }
  const real = changes.filter((c) => c.before !== undefined || c.after !== undefined);
  if (!real.length) return;
  void queue(label, toOps(real), real.map((c) => c.before ?? null));
  undoStack.value = [...undoStack.value, { label, changes: real }].slice(-LIMIT);
  redoStack.value = [];
}

const invert = (changes: Change[]): Change[] => [...changes].reverse().map((c) => ({ type: c.type, before: c.after, after: c.before }));

export function undo(): void {
  const a = undoStack.value.at(-1);
  if (!a || show.value.lock) return;
  undoStack.value = undoStack.value.slice(0, -1);
  redoStack.value = [...redoStack.value, a];
  const inverse = invert(a.changes);
  void queue(`Undo: ${a.label}`, toOps(inverse), inverse.map((c) => c.before ?? null));
  notify(`Undone: ${a.label}`);
}

export function redo(): void {
  const a = redoStack.value.at(-1);
  if (!a || show.value.lock) return;
  redoStack.value = redoStack.value.slice(0, -1);
  undoStack.value = [...undoStack.value, a];
  void queue(`Redo: ${a.label}`, toOps(a.changes), a.changes.map((c) => c.before ?? null));
  notify(`Redone: ${a.label}`);
}

/** After a restore or another person's change, stale undo steps would only conflict: drop them. */
export function clearHistory(): void {
  undoStack.value = [];
  redoStack.value = [];
}
